import { useEffect, useMemo, useRef } from "react"
import { Line } from "react-chartjs-2"
import { Chart, ChartEvent, registerables, ScriptableContext, TooltipItem, TooltipModel } from "chart.js"
import annotationPlugin from "chartjs-plugin-annotation"
import classNames from "classnames"
import dayjs from "dayjs"
import dayjs_plugin_timezone from "dayjs/plugin/timezone"

import { MonitoringReportAnnotationItem, MonitoringReportAnnotations } from "types/MonitoringReportAnnotations"

import { hexToRGBA } from "utils/hexToRGBA"
import { useWindowResize } from "hooks/useWindowResize"

import { NoDataLayer } from "../../components/chart/NoDataLayer"
import { ApiTypes } from "../../types"
import getLegendMarginPlugin from "../../utils/chartjs/getLegendMarginPlugin"
import { borderColorWithSkipped, borderDashWithSkipped, defaultChartTooltipOptions } from "../../utils/chartUtils"
import { getRoundedTemperature } from "../../utils/getRoundedTemperature"
import parseCelsiusToFahrenheit from "../../utils/parseCelsiusToFahrenheit"
import { getMaxDivider } from "./getMaxDivider"
import getScheduleDataArray from "./getScheduleDataArray"
import ScheduleComponents from "./ScheduleComponents"
import { Title } from "./styled"

import styles from "./index.module.scss"

dayjs.extend(dayjs_plugin_timezone)

Chart.register(...registerables)
Chart.register(annotationPlugin)

interface Props {
  isCelsius: boolean
  settings: {
    ticksCount: number
    dataCountsDivider: number
  }
  historicalData: ApiTypes.Res.MonitoringHistoricalDataBucket[]
  isSingleDataPoint: boolean
  showSchedule: boolean
  intervalInMinutes: number
  monitoringReportAnnotations?: MonitoringReportAnnotations | null
  timeZone: string
}

const TemperatureLineChart = ({
  isCelsius,
  settings,
  historicalData,
  isSingleDataPoint,
  showSchedule,
  intervalInMinutes,
  monitoringReportAnnotations,
  timeZone,
}: Props) => {
  const dimensions = useRef({ width: 0, height: 0, gradient: [] as CanvasGradient[] })
  const containerRef = useRef<HTMLDivElement | null>(null)
  const originDivRef = useRef<HTMLDivElement | null>(null)
  const isLargeScreen = useWindowResize()
  const getTemperature = (value: number | null) => {
    if (value === null) {
      return null
    }

    return isCelsius ? value : parseCelsiusToFahrenheit(value)
  }

  useEffect(() => {
    dimensions.current.width = 0
    dimensions.current.height = 0
    dimensions.current.gradient = []
  }, [settings])

  const calculateAnnotationPosition = (date: string) => {
    if (!monitoringReportAnnotations) return 0
    const startDate = dayjs(monitoringReportAnnotations.start)
    const stopDate = dayjs(monitoringReportAnnotations.stop)
    const issueDate = dayjs(date)
    const issuePosition = issueDate.diff(startDate, "minutes")
    const twoDayInterval = stopDate.diff(startDate, "days") === 1

    return issuePosition / (twoDayInterval ? 30 : 10)
  }

  const getAnnotationProps = (annotation: MonitoringReportAnnotationItem) => {
    const type = annotation.start === annotation.stop ? ("line" as const) : ("box" as const)

    return {
      type,
      xMin: calculateAnnotationPosition(annotation.start),
      xMax: calculateAnnotationPosition(annotation.stop),
      borderColor: annotation.color,
      backgroundColor: annotation.color,
      borderWidth: 1,
      borderDash: [5, 1],
    }
  }

  const parsedData =
    historicalData.map(item => ({
      temperature: getTemperature(item["avg#average_current_temperature_embedded"].value),
      setTemperature: getTemperature(item["avg#average_set_temperature"].value),
      autoHigh: getTemperature(item["avg#average_auto_high"]?.value || null),
      autoLow: getTemperature(item["avg#average_auto_low"]?.value || null),
      timestamp: item.key_as_string,
    })) || []
  const autoModeUsagePercentageList = useMemo(() => {
    return historicalData.map(item =>
      item.doc_count ? item["filters#running_mode_count"].buckets.auto.doc_count / item.doc_count : 0,
    )
  }, [parsedData])

  const scheduleData = useMemo(() => {
    if (!showSchedule) return []

    return getScheduleDataArray(historicalData, intervalInMinutes)
  }, [historicalData, showSchedule, intervalInMinutes])

  const minTemperatureValue = useMemo(() => {
    if (!showSchedule) {
      return null
    }

    const allTemperatures: (number | null)[] = []
    parsedData.forEach(item => {
      if (item.temperature !== null) allTemperatures.push(item.temperature)
      if (item.setTemperature !== null) allTemperatures.push(item.setTemperature)
      if (item.autoHigh !== null) allTemperatures.push(item.autoHigh)
      if (item.autoLow !== null) allTemperatures.push(item.autoLow)
    })
    if (allTemperatures.length === 0) return null
    const minValue = Math.min(...(allTemperatures.filter(v => v !== null) as number[]))
    const maxValue = Math.max(...(allTemperatures.filter(v => v !== null) as number[]))
    const range = maxValue - minValue
    const bufferPercentage = 0.2
    const minBuffer = isCelsius ? 2 : 3.6
    const buffer = Math.max(range * bufferPercentage, minBuffer)

    const adjustedMin = minValue - buffer
    const roundedMin = Math.floor(adjustedMin / 5) * 5

    return roundedMin
  }, [parsedData, isCelsius])

  const showOnLabel = (i: number, isAuto: boolean) => {
    const item = historicalData[i]

    if (item.doc_count) {
      if (isAuto) {
        return item["filters#running_mode_count"].buckets.auto.doc_count > 0
      }

      return item.doc_count > item["filters#running_mode_count"].buckets.auto.doc_count
    }

    return false
  }

  const [currentTemperatureList, setTemperatureList, autoMaxTemperatureList, autoMinTemperatureList] = useMemo(() => {
    return [
      parsedData.map(item => item.temperature),
      parsedData.map((item, i) => ({ temperature: item.setTemperature, showOnLabel: showOnLabel(i, false) })),
      parsedData.map((item, i) => ({ temperature: item.autoHigh, showOnLabel: showOnLabel(i, true) })),
      parsedData.map((item, i) => ({ temperature: item.autoLow, showOnLabel: showOnLabel(i, true) })),
    ]
  }, [parsedData])
  const noData = parsedData.length === 0

  const temperatureMark = isCelsius ? "°C" : "°F"

  const commonDatasetValues = {
    fill: "start",
    backgroundColor: "transparent",
    pointStyle: isSingleDataPoint as unknown as "circle",
    spanGaps: true,
    segment: {
      borderDash: borderDashWithSkipped,
      borderColor: borderColorWithSkipped,
    },
    borderWidth: 2,
  }

  const getGradient = (chart: ScriptableContext<"line">, color: string, isAuto = false) => {
    const { ctx, chartArea } = chart.chart
    const chartWidth = chartArea.right - chartArea.left
    const chartHeight = chartArea.bottom - chartArea.top
    if (
      !dimensions.current.gradient[chart.datasetIndex] ||
      dimensions.current.width !== chartWidth ||
      dimensions.current.height !== chartHeight
    ) {
      dimensions.current.width = chartWidth
      dimensions.current.height = chartHeight
      const gradient = ctx.createLinearGradient(chartArea.left, 0, chartArea.right, 0)

      for (let i = 0; i < autoModeUsagePercentageList.length; i++) {
        // This chunk fix the case when next value is null
        if (setTemperatureList[i] === null && i > 0) {
          const alpha = Math.max(
            isAuto ? autoModeUsagePercentageList[i - 1] : 1 - autoModeUsagePercentageList[i - 1],
            0.05,
          )
          gradient.addColorStop(i / autoModeUsagePercentageList.length, hexToRGBA(color, alpha))
        } else {
          const alpha = Math.max(isAuto ? autoModeUsagePercentageList[i] : 1 - autoModeUsagePercentageList[i], 0.05)
          gradient.addColorStop(i / autoModeUsagePercentageList.length, hexToRGBA(color, alpha))
        }
      }
      dimensions.current.gradient[chart.datasetIndex] = gradient
    }

    return dimensions.current.gradient[chart.datasetIndex]
  }

  const data = {
    labels: [...parsedData.map(item => item.timestamp)],
    datasets: [
      {
        ...commonDatasetValues,
        label: "Current temperature",
        data: currentTemperatureList,
        borderColor: "#43E0F8",
        pointBackgroundColor: "#43E0F8",
      },
      {
        ...commonDatasetValues,
        label: "Set temperature",
        data: setTemperatureList as unknown as (number | null)[],
        borderColor: (context: ScriptableContext<"line">) => context.chart.chartArea && getGradient(context, "#22c55e"),
        pointBackgroundColor: "#22c55e",
        parsing: {
          yAxisKey: "temperature",
          xAxisKey: "showOnLabel",
        },
      },
      {
        ...commonDatasetValues,
        label: "Cool to temp",
        data: autoMaxTemperatureList as unknown as (number | null)[],
        borderColor: (context: ScriptableContext<"line">) =>
          context.chart.chartArea && getGradient(context, "#2463eb", true),
        pointBackgroundColor: "#2463eb",
        parsing: {
          yAxisKey: "temperature",
          xAxisKey: "showOnLabel",
        },
      },
      {
        ...commonDatasetValues,
        label: "Heat to temp",
        data: autoMinTemperatureList as unknown as (number | null)[],
        borderColor: (context: ScriptableContext<"line">) =>
          context.chart.chartArea && getGradient(context, "#b91c1c", true),
        pointBackgroundColor: "#b91c1c",
        parsing: {
          yAxisKey: "temperature",
          xAxisKey: "showOnLabel",
        },
      },
      ...(parsedData.length === 0
        ? [
            {
              label: "",
              data: [74, 75, 76, 77, 78],
            },
          ]
        : []),
    ],
  }

  const options = {
    maintainAspectRatio: false,
    interaction: {
      mode: "index" as const,
    },
    plugins: {
      legend: {
        display: !noData,
        position: isLargeScreen ? ("top" as const) : ("bottom" as const),
        align: isLargeScreen ? ("end" as const) : ("start" as const),
        labels: {
          color: "#94A3B8",
          usePointStyle: true,
          pointStyle: "rect",
        },
      },
      tooltip: {
        ...defaultChartTooltipOptions,
        displayColors: true,
        callbacks: {
          title: function (this: TooltipModel<"line">, tooltipItem: TooltipItem<"line">[]) {
            const date = dayjs(tooltipItem[0].label).tz(timeZone || "UTC")
            return date.format("YYYY-MM-DD HH:mm")
          },
          label: function (this: TooltipModel<"line">, tooltipItem: TooltipItem<"line">) {
            const showOnLabel = (tooltipItem.raw as { showOnLabel: boolean }).showOnLabel
            if (showOnLabel || showOnLabel === undefined) {
              return `${getRoundedTemperature(tooltipItem.formattedValue, 2)}${temperatureMark}`
            }

            return ""
          },
        },
      },
      annotation: monitoringReportAnnotations
        ? {
            annotations: monitoringReportAnnotations.items.map(getAnnotationProps),
          }
        : undefined,
    },
    scales: {
      x: {
        border: {
          color: "#e2e8f0",
        },
        grid: {
          color: "#e2e8f0",
        },
        ticks: {
          maxRotation: isLargeScreen ? 0 : 60,
          maxTicksLimit: isLargeScreen ? settings.ticksCount : getMaxDivider(settings.ticksCount),
          callback: (value: number | string) => Number(value) / settings.dataCountsDivider,
          color: "#94A3B8",
        },
      },
      y: {
        position: "left" as const,
        min: minTemperatureValue !== null && scheduleData ? minTemperatureValue : undefined,
        border: {
          display: false,
          color: "rgba(0,0,0,0)",
        },
        ticks: {
          maxTicksLimit: 6,
          stepSize: parsedData.length === 0 ? 1 : 0.1,
          callback: function (value: number | string) {
            return `${getRoundedTemperature(value, 1)}${temperatureMark}`
          },
          color: "#94A3B8",
        },
      },
    },
    onHover: (ctx: ChartEvent) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const chart = (ctx as any).chart
      chart.data.datasets[0].pointStyle = "circle"

      chart.data.datasets[1].pointStyle = chart.data.datasets[1].data.map((_: unknown, i: number) =>
        chart.data.datasets[1].data[i]?.showOnLabel ? "circle" : false,
      )
      chart.data.datasets[2].pointStyle = chart.data.datasets[2].data.map((_: unknown, i: number) =>
        chart.data.datasets[2].data[i]?.showOnLabel ? "circle" : false,
      )
      chart.data.datasets[3].pointStyle = chart.data.datasets[3].data.map((_: unknown, i: number) =>
        chart.data.datasets[3].data[i]?.showOnLabel ? "circle" : false,
      )
    },
  }

  const originOverlayPlugin = useMemo(
    () => ({
      id: "originOverlay",
      afterLayout: (chart: Chart) => {
        const area = chart.chartArea
        const div = originDivRef.current
        const container = containerRef.current
        if (!area || !div || !container) return
        div.style.left = `${area.left}px`
        div.style.width = `${area.right - area.left}px`
        div.style.top = `${area.bottom - div.offsetHeight}px`
      },
    }),
    [],
  )

  return (
    <>
      {!noData && !isLargeScreen && <Title>Temperature</Title>}
      <div
        ref={containerRef}
        className={classNames(styles.chartContainer, styles.temperatureLineChartContainer, {
          [styles.chartContainerWithLabel]: !noData,
          [styles.emptyReportDataWrapper]: noData,
        })}
      >
        {noData && <NoDataLayer />}
        <Line data={data} options={options} plugins={[getLegendMarginPlugin(15), originOverlayPlugin]} />
        {showSchedule && (
          <div ref={originDivRef} className={styles.chartBottomDiv}>
            <ScheduleComponents
              overallDocsCount={historicalData.length * intervalInMinutes}
              scheduleData={scheduleData}
            />
          </div>
        )}
      </div>
    </>
  )
}

export default TemperatureLineChart
